import {
    Alert,
    Box,
    Button,
    Chip,
    Grid2,
    IconButton,
    Menu,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetBookingItems } from '@openapi/booking-items/booking-items';
import { useGetOrders, manuallyTriggerRollback } from '@openapi/orders/orders';
import { changeBookingItems, useGetBookingTicketPermissions } from '@openapi/bookings/bookings';
import { useGetShow } from '@openapi/shows/shows';
import {
    useGetTimeslots,
    useGetTimeslotPrices,
    useGetTimeslotDynamicPrices,
} from '@openapi/timeslots/timeslots';
import useNotify from '../../../../hooks/useNotify';
import {
    getMatrixEntry,
    matrixHasExplicitEnabledFlags,
} from '../../../../helpers/commissionMatrixUtils';
import BlockWithTitle from '../../../../components/atom/BlockWithTitle';
import {
    useGetTicketSeats,
    useGetFreeTicketSeats,
    getFreeTicketSeats,
    getAutoSeating,
} from '@openapi/ticket-seats/ticket-seats';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import { MODAL_TYPE, useModals } from '../../../../hooks/useModal';
import { useCapabilities } from '../../../../hooks/useCapabilities';
import { useAuth } from '../../../../hooks/useAuth';
import formatErrorToString from '../../../../helpers/formatErrorToString';
import { BOOKING_STATUSES } from '../../../../helpers/constants';
import { formatDateTimeInVenueTz } from '../../../../helpers/formatDateTime';
import { useSlotTemplate } from '../../../../hooks/useSlotTemplate';
import { resolveSlotLabel, slotOrderIndex } from '../../../../helpers/slotTemplate';
import OneTicket from './OneTicket';
import OneTicketEdit from './OneTicketEdit';
import OneTicketEditPopup from './OneTicketEditPopup';
import BulkAddTicketsWizard from './BulkAddTicketsWizard';
import SimplifiedTicketManager from './SimplifiedTicketManager';
import mapFromDbToElement from '../../../seats/helpers/mapFromDbToElement';
import SvgViewer from '../../../../components/svgeditor/SvgViewer';
import { useArrangementElements } from '../../../../hooks/useArrangementElements';
import TimeslotCalendar from '../../../../components/TimeslotCalendar';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import { useGetTicketOptions } from '@openapi/ticket-options/ticket-options';
import { definePrice, previewItemFinalPriceCents } from '../../../../helpers/calculateShowFees';
import { getPriceFromMatrix, buildPriceOverrideMap } from './ticketValidationHelpers';

/**
 * interface BookingItem {
 *   id: string;
 *   ticketSeatName: string;
 *   ticketOptionName: string;
 *   ticketTypeName: string;
 *   price: number; // in cents
 *   customPriceCents: string; // for editing, in cents
 *   note: string;
 *   checkinNote: string;
 *   shouldDelete: boolean; // for marking items to be deleted
 *   oldId?: string; // for keeping track of old IDs when editing
 *   ticketTypeCanBuyAlone?: boolean; // whether the ticket type can be bought alone
 * }
 */

/**
 * Generate a list of changes to show to agents for a doublecheck
 * Group it by tickets, so it's simpler to read
 * @param {BookingItem[]} oldItems
 * @param {BookingItem[]} newItems
 */
function generateChangeList(
    oldItems,
    newItems,
    showFees = [],
    isOnline,
    netRate = false,
    commissionMatrix = {},
) {
    const getOldticketName = (id) => {
        const o = oldItems.find((item) => item.id === id);
        if (!o) {
            return `Booking item #${id}`;
        }
        if (o.ticketSeatName) {
            return o.ticketSeatName;
        }
        if (o.ticketOptionName && o.ticketTypeName) {
            return `${o.ticketOptionName} (${o.ticketTypeName})`;
        }
        return `Booking item #${id}`;
    };

    const blocks = [];
    for (let i = 0; i < newItems.length; i++) {
        const newItem = newItems[i];
        if (newItem.oldId) {
            // this is an existing item
            if (newItem.shouldDelete) {
                // this item is marked for deletion
                blocks.push(`${getOldticketName(newItem.oldId)} removed`);
            } else {
                // this item is being edited
                const oldItem = oldItems.find((item) => item.id === newItem.oldId);
                if (oldItem) {
                    const changes = [];
                    if (oldItem.ticketSeatName !== newItem.ticketSeatName) {
                        changes.push(
                            `Seat changed from "${oldItem.ticketSeatName || 'No ticket'}" to "${newItem.ticketSeatName}"`,
                        );
                    }
                    if (oldItem.ticketOptionName !== newItem.ticketOptionName) {
                        changes.push(
                            `Option changed from "${oldItem.ticketOptionName}" to "${newItem.ticketOptionName}"`,
                        );
                    }
                    if (oldItem.ticketTypeName !== newItem.ticketTypeName) {
                        changes.push(
                            `Type changed from "${oldItem.ticketTypeName}" to "${newItem.ticketTypeName}"`,
                        );
                    }
                    if (oldItem.price !== newItem.customPriceCents) {
                        const oldFinal = oldItem.finalPrice;
                        // Net rate shows retail minus commission; retail path
                        // shows retail plus fees/taxes.
                        const newFinal = previewItemFinalPriceCents(newItem, {
                            netRate,
                            commissionMatrix,
                            showFees,
                            isOnline,
                        });
                        changes.push(
                            `Price changed from ${formatCentsToUsd(oldItem.price)} to ${formatCentsToUsd(
                                newItem.customPriceCents,
                            )} (Final price from ${formatCentsToUsd(oldFinal)} to ${formatCentsToUsd(newFinal)})`,
                        );
                    }
                    if (oldItem.note !== newItem.note) {
                        changes.push(`Note changed`);
                    }
                    if (oldItem.checkinNote !== newItem.checkinNote) {
                        changes.push(`Check-in note changed`);
                    }
                    if (changes.length > 0) {
                        blocks.push(`${getOldticketName(newItem.oldId)}: ${changes.join(', ')}`);
                    }
                }
            }
        } else {
            // this is a new item. Net rate shows the net price actually charged
            // (retail minus commission); retail shows the entered price.
            const newItemPrice = netRate
                ? previewItemFinalPriceCents(newItem, {
                      netRate,
                      commissionMatrix,
                      showFees,
                      isOnline,
                  })
                : newItem.customPriceCents;
            blocks.push(
                `New booking item: ${newItem.ticketSeatName || ''} ${newItem.ticketOptionName || ''} (${newItem.ticketTypeName || ''}) at ${formatCentsToUsd(newItemPrice)}`,
            );
        }
    }
    return blocks;
}

const EMPTY_FEES = [];

function createEmptyTicketItem(seatId = '', seatName = '') {
    return {
        id: Math.random().toString(36).substring(2, 15),
        ticketSeatName: seatName || '',
        ticketSeatId: seatId || '',
        ticketOptionId: null,
        ticketOptionName: '',
        ticketTypeId: null,
        ticketTypeName: '',
        price: 0,
        // the customPriceCents should match up with the price at the start
        customPriceCents: '0',
        note: '',
        checkinNote: '',
        shouldDelete: false,
    };
}

/**
 * Generate color map for seats in booking edit mode
 * @param {Array} elements - The seat elements
 * @param {Array} data - The booking items data
 * @param {number|null} selectedSeatId - Currently selected seat ID
 * @param {Array} freeSeatsData - Array of free seat IDs
 * @return {Object} - Object mapping seat IDs to colors
 */
function generateSeatColorMap(elements, data, selectedSeatId, freeSeatsData, ticketTypes) {
    const colorMap = {};

    elements.forEach((element) => {
        // Check if this seat has a booking item
        const seat = data.find((item) => item.ticketSeatId === element.id);
        const freeOverall = freeSeatsData?.ticketSeats?.find((s) => s.id === element.id);

        // Check seat-to-ticket-type mismatch
        const isMismatch =
            seat &&
            !seat.shouldDelete &&
            (() => {
                const tt = (ticketTypes || []).find((t) => t.id === seat.ticketTypeId);
                if (!tt?.seats?.length) return false;
                return !tt.seats.some((s) => Number(s.id) === Number(seat.ticketSeatId));
            })();

        // Selected seat - highlight it
        if (element.id === selectedSeatId) {
            colorMap[element.id] = isMismatch ? '#ef5350' : '#ffff00';
        }
        // Non-bookable seats
        else if (!element.bookable) {
            colorMap[element.id] = '#eeeeee'; // Light gray for non-bookable items (like tables, etc.)
        }
        // Booked but marked for deletion
        else if (seat && seat.shouldDelete) {
            colorMap[element.id] = '#941007ff'; // Red for booked but to be deleted
        }
        // Booked with ticket type mismatch
        else if (seat && isMismatch) {
            colorMap[element.id] = '#d32f2f'; // MUI error.main for mismatch
        }
        // Booked seats on this booking
        else if (seat) {
            colorMap[element.id] = '#4caf50'; // Green for booked
        }
        // Booked seats on other bookings
        else if (freeOverall === undefined) {
            colorMap[element.id] = '#eeeeee'; // Light gray for booked on other bookings
        }
        // Free seats (available for booking)
        else {
            colorMap[element.id] = '#2196f3'; // Blue for free/available
        }
    });

    return colorMap;
}

function mapBookingItemsToUpdated(items) {
    return (items || []).map((item) => ({
        ...item,
        customPriceCents: item.price,
        shouldDelete: false,
        oldId: item.id,
    }));
}

// Snapshot the agent's current pending modification from data already in the
// page (the modification has been applied to the DB, so the booking items
// reflect the post-modification state). The seed is captured BEFORE we roll
// the order back so we can rehydrate the editor with what the agent had.
function buildEditorSeed(pendingOrders, bookingItems) {
    if (!pendingOrders?.length) return null;
    const byId = new Map((bookingItems || []).map((b) => [b.id, b]));
    const addedIds = pendingOrders.flatMap((o) => o.ticketsAdded || []);
    const changedIds = pendingOrders.flatMap((o) => o.ticketsChanged || []);
    const removedIds = pendingOrders.flatMap((o) => o.ticketsRemoved || []);
    const snap = (id) => {
        const b = byId.get(id);
        if (!b) return null;
        return {
            id: b.id,
            ticketTypeId: b.ticketTypeId,
            ticketOptionId: b.ticketOptionId,
            ticketSeatId: b.ticketSeatId,
            price: b.price,
            note: b.note ?? '',
            checkinNote: b.checkinNote ?? '',
        };
    };
    return {
        addedItems: addedIds.map(snap).filter(Boolean),
        changedItems: changedIds.map(snap).filter(Boolean),
        removedItemIds: removedIds,
    };
}

function lookupSeedNames(ticketTypes, ticketOptions, seatList, ttId, toId, tsId) {
    const tt = ticketTypes.find((x) => x.id === ttId);
    const to = ticketOptions.find((x) => x.id === toId);
    const seat = seatList.find((x) => x.id === tsId);
    return {
        ticketTypeName: tt?.name || '',
        ticketTypeCanBuyAlone: tt?.canBuyAlone ?? true,
        ticketOptionName: to?.name || '',
        ticketSeatName: seat?.name || '',
    };
}

// Merge the captured seed onto the post-rollback booking items so the editor
// reopens with the agent's pending modification: changedItems overwrite values
// on matching oldIds, removedItemIds mark existing entries shouldDelete,
// addedItems append as new editor rows.
function applyEditorSeed(baseItems, seed, ticketTypes, ticketOptions, seatList) {
    if (!seed) return baseItems;
    const changedById = new Map((seed.changedItems || []).map((it) => [it.id, it]));
    const removedIds = new Set(seed.removedItemIds || []);
    const updated = baseItems.map((item) => {
        if (item.oldId == null) return item;
        if (removedIds.has(item.oldId)) {
            return { ...item, shouldDelete: true };
        }
        const changed = changedById.get(item.oldId);
        if (!changed) return item;
        return {
            ...item,
            ticketTypeId: changed.ticketTypeId,
            ticketOptionId: changed.ticketOptionId,
            ticketSeatId: changed.ticketSeatId,
            price: changed.price,
            customPriceCents: changed.price,
            note: changed.note || '',
            checkinNote: changed.checkinNote || '',
            ...lookupSeedNames(
                ticketTypes,
                ticketOptions,
                seatList,
                changed.ticketTypeId,
                changed.ticketOptionId,
                changed.ticketSeatId,
            ),
        };
    });
    const additions = (seed.addedItems || []).map((item) => ({
        id: Math.random().toString(36).substring(2, 15),
        ticketTypeId: item.ticketTypeId,
        ticketOptionId: item.ticketOptionId,
        ticketSeatId: item.ticketSeatId,
        price: item.price,
        customPriceCents: item.price,
        note: item.note || '',
        checkinNote: item.checkinNote || '',
        shouldDelete: false,
        oldId: null,
        ...lookupSeedNames(
            ticketTypes,
            ticketOptions,
            seatList,
            item.ticketTypeId,
            item.ticketOptionId,
            item.ticketSeatId,
        ),
    }));
    return [...updated, ...additions];
}

// Apply an upgrade offer composition to the current booking items.
// Each composition entry maps (fromTicketTypeId, fromTicketOptionId) → (toTicketTypeId, toTicketOptionId) for `quantity` items.
function applyUpgradeComposition(items, composition, ticketTypes, ticketOptions) {
    const pending = new Map();
    for (const comp of composition) {
        const key = `${comp.fromTicketTypeId}:${comp.fromTicketOptionId}`;
        if (!pending.has(key)) pending.set(key, []);
        for (let i = 0; i < comp.quantity; i++) {
            pending.get(key).push({
                toTicketTypeId: comp.toTicketTypeId,
                toTicketOptionId: comp.toTicketOptionId,
            });
        }
    }
    return items.map((item) => {
        if (item.shouldDelete) return item;
        const key = `${item.ticketTypeId}:${item.ticketOptionId}`;
        const upgrades = pending.get(key);
        if (!upgrades || upgrades.length === 0) return item;
        const upgrade = upgrades.shift();
        const names = lookupSeedNames(
            ticketTypes,
            ticketOptions,
            [],
            upgrade.toTicketTypeId,
            upgrade.toTicketOptionId,
            null,
        );
        const tt = ticketTypes.find((x) => x.id === upgrade.toTicketTypeId);
        // Price is pre-validated in the seeding effect, so the matrix lookup
        // resolves here; no fallback to the old price (that would undercharge).
        const newPrice = getPriceFromMatrix(names.ticketOptionName, tt?.priceMatrix);
        return {
            ...item,
            ticketTypeId: upgrade.toTicketTypeId,
            ticketOptionId: upgrade.toTicketOptionId,
            ticketSeatId: null,
            ticketSeatName: '',
            ticketTypeName: names.ticketTypeName,
            ticketTypeCanBuyAlone: names.ticketTypeCanBuyAlone,
            ticketOptionName: names.ticketOptionName,
            price: newPrice,
            customPriceCents: newPrice,
        };
    });
}

function computeCapacityOverflows({ updatedData, blocks, t, timeslotIsChanging = false }) {
    const added = {};
    const deleted = {};
    updatedData.forEach((item) => {
        if (!item.ticketTypeId) return;
        if (item.shouldDelete && item.oldId) {
            // When moving to a new timeslot, deletions don't offset — every
            // kept item lands on the new timeslot as an addition. Mirrors the
            // backend logic in ChangeBookingItems.
            if (!timeslotIsChanging) {
                deleted[item.ticketTypeId] = (deleted[item.ticketTypeId] || 0) + 1;
            }
        } else if (!item.shouldDelete && (timeslotIsChanging || !item.oldId)) {
            added[item.ticketTypeId] = (added[item.ticketTypeId] || 0) + 1;
        }
    });
    const overflows = [];
    for (const ticketTypeIdStr of Object.keys(added)) {
        const ticketTypeId = Number(ticketTypeIdStr);
        const netAdd = (added[ticketTypeId] || 0) - (deleted[ticketTypeId] || 0);
        if (netAdd <= 0) continue;
        const block = blocks.find((b) => b.ticketTypeId === ticketTypeId);
        if (!block) continue;
        if (block.available < netAdd) {
            overflows.push(
                t('bookings.capacityWarningDetail', {
                    ticketType: block.ticketTypeName,
                    adding: netAdd,
                    available: Math.max(0, block.available),
                }),
            );
        }
    }
    return overflows;
}

const BookingItemsDetails = forwardRef(function BookingItemsDetails(
    {
        bookingId,
        booking,
        reloadData,
        reloadParentOrders,
        limitedModification,
        requestTimeslotEdit,
        onTimeslotEditHandled,
        readOnly,
        bookingUnlocked,
        setBookingUnlocked,
        onCancelReservation,
        onRefundReservation,
        canCancel,
        canRefund,
        operatingDisabled,
        onPendingChangesChange,
    },
    ref,
) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { notifyError } = useNotify();
    const { showModal } = useModals();
    const { can, CAPABILITIES: CAP } = useCapabilities();
    const { user: currentUser } = useAuth();
    const canModifySeating = can(CAP.SEATING_MODIFY);
    const canCrossTicketType = can(CAP.SEATING_CROSS_TICKET_TYPE);
    const canOverbook = can(CAP.BOOKINGS_OVERBOOK);
    const canCrossSelling = can(CAP.BOOKINGS_CROSS_SELLING);
    const hasSimplifiedCapability = can(CAP.BOOKINGS_SIMPLIFIED_TICKETS);
    // Without SEATING_CROSS_TICKET_TYPE the editor defaults to simplified (toggleable);
    // agents (no users.manage, not superadmin) with BOOKINGS_SIMPLIFIED_TICKETS are locked in.
    const lockedToSimplified =
        hasSimplifiedCapability && !can(CAP.USERS_MANAGE) && !currentUser?.isSuperAdmin;
    const defaultSimplified = !canCrossTicketType;
    const [simplifiedOverride, setSimplifiedOverride] = useState(defaultSimplified);
    const useSimplifiedTickets = lockedToSimplified || simplifiedOverride;

    const showId = booking?.timeslot?.showId;
    const originalTimeslotId = booking?.timeslotId;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [mapPopupVisible, setMapPopupVisible] = useState(false);

    const [updatedData, setUpdatedData] = useState([]);

    useEffect(() => {
        setSaveError(null);
    }, [updatedData]);
    const [elements, setElements] = useState([]);

    // Timeslot change state. The active timeslot is the new one if changed,
    // otherwise the original - resolve it before the arrangement overlay so a
    // per-timeslot rainout override is reflected, not just the show default.
    const [newTimeslotId, setNewTimeslotId] = useState(null);
    const timeslotId = newTimeslotId || originalTimeslotId;

    // Price overrides that seed the default price for newly added/edited tickets
    // so admin sees the price that will be charged, not the show-level default.
    // Dynamic pricing (external provider) wins when enabled; otherwise the
    // per-timeslot custom prices apply.
    //
    // staleTime/refetch overrides: dynamic pricing is volatile (a random provider
    // can return a different price every call), so without this every ticket
    // added during one editing sitting could get re-priced independently (e.g. on
    // a tab-focus refetch, since the app default is staleTime:0 everywhere). All
    // tickets added while this booking is open should share one resolved price
    // per combo; reopening the booking later (remount) is a new sitting and is
    // allowed to pick up a fresh price.
    const { data: dynamicPricesData } = useGetTimeslotDynamicPrices(
        Number(timeslotId),
        undefined,
        {
            query: {
                enabled: !!timeslotId,
                staleTime: 30 * 60 * 1000,
                gcTime: 30 * 60 * 1000,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    );
    const dynamicEnabled = !!dynamicPricesData?.enabled;

    const { data: timeslotPricesData } = useGetTimeslotPrices(Number(timeslotId), {
        query: { enabled: !!timeslotId && !dynamicEnabled },
    });

    const priceOverrides = useMemo(
        () =>
            dynamicEnabled
                ? buildPriceOverrideMap(dynamicPricesData)
                : buildPriceOverrideMap(timeslotPricesData),
        [dynamicEnabled, dynamicPricesData, timeslotPricesData],
    );

    const { displayElements, excludedSeatIds } = useArrangementElements(
        showId,
        elements,
        timeslotId,
    );
    const [selectedSeatId, setSelectedSeatId] = useState(null);
    const [editTicketIdx, setEditTicketIdx] = useState(null);

    const [showTimeslotPicker, setShowTimeslotPicker] = useState(false);

    // Header menu
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    // Handle external request to open timeslot edit (from pencil icon on top bar)
    useEffect(() => {
        if (!requestTimeslotEdit) return;
        if (onTimeslotEditHandled) onTimeslotEditHandled();
        if (!bookingUnlocked) {
            setBookingUnlocked(true);
        }
        setShowTimeslotPicker(true);
    }, [requestTimeslotEdit]); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadBookingItems,
    } = useGetBookingItems(
        {
            optionalId: Number(bookingId),
            pageSize: 10000,
        },
        {
            query: {
                onError: (err) => {
                    notifyError(err);
                    navigate('/bookings');
                },
            },
        },
    );

    const { data: freeSeatsData } = useGetFreeTicketSeats(showId, {
        timeslotId,
    });

    // Fetch timeslots for the show (used for timeslot change calendar)
    const { data: timeslotsData } = useGetTimeslots(
        {
            pageSize: 10000,
            optionalId: Number(showId),
            sort: 'starttime',
            sortDir: 'ASC',
            includeBlocks: true,
        },
        {
            query: {
                enabled: !!showId && bookingUnlocked,
            },
        },
    );

    const { template: slotTemplate } = useSlotTemplate(showId);

    const { data: seatData, isLoading: seatLoading } = useGetTicketSeats(showId, {
        pageSize: 10000,
        page: 0,
    });

    const { data: allTicketTypes } = useGetTicketTypes(
        {
            optionalId: Number(showId),
            page: 0,
            pageSize: 1000,
            includeSeats: true,
            onlyShowInList: true,
            sort: 'priority',
            sortDir: 'ASC',
        },
        {
            query: {
                enabled: !!showId,
                onError: notifyError,
            },
        },
    );

    const { data: allTicketOptions } = useGetTicketOptions(
        {
            optionalId: Number(showId),
            page: 0,
            pageSize: 1000,
            sort: 'priority',
            sortDir: 'ASC',
        },
        {
            query: {
                enabled: !!showId,
                onError: notifyError,
            },
        },
    );

    // --- Rental period change (booking-level) ------------------------------
    // Convert the whole booking to a sibling period in the same rental-day
    // group. Mixing is banned, so every period-scoped ticket moves at once;
    // the price diff and seat (re)assignment ride on the existing change flow.
    // Stack the period control vertically on phones - the labels
    // ("Afternoon (PM)") are too long to sit side by side at ~320px.
    const isNarrowScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const currentTimeslotRecord = (timeslotsData?.timeslots || []).find(
        (ts) => ts.id === timeslotId,
    );
    // The booking's rental slot, null on a non-rental timeslot so the slot
    // filter below is a no-op. Read off the show's timeslot list rather than
    // refetching this one timeslot: every consumer sits behind
    // `bookingUnlocked`, which is exactly when that list is loaded.
    const targetSlotKey = currentTimeslotRecord?.slotKey ?? null;
    const rentalGroupId = currentTimeslotRecord?.timeslotGroupId ?? null;
    const periodTimeslots = {};
    if (rentalGroupId != null) {
        for (const ts of timeslotsData?.timeslots || []) {
            if (ts.timeslotGroupId === rentalGroupId && ts.slotKey) {
                periodTimeslots[ts.slotKey] = ts;
            }
        }
    }
    // Available slot keys for this rental group, ordered by template position.
    const availablePeriods = Object.keys(periodTimeslots).sort(
        (a, b) => slotOrderIndex(slotTemplate, a) - slotOrderIndex(slotTemplate, b),
    );
    const currentPeriod =
        availablePeriods.find((p) => periodTimeslots[p]?.id === timeslotId) ?? null;
    const showPeriodSwitcher =
        availablePeriods.length > 1 &&
        !!currentPeriod &&
        !limitedModification &&
        !booking?.invoiceId &&
        booking?.status !== BOOKING_STATUSES.CANCELLED;

    // Remap every live ticket to a target rental period: keep the type, swap the
    // option to that period's option, and reprice from the matrix. A seat stays
    // assigned when it is still available in the target period (an AM-to-Full-Day
    // upgrade keeps the guest's seat); otherwise it is cleared for re-assignment.
    // Returns the new items, any types with no price for the target period
    // (which block the change), and the net diff.
    const remapItemsToPeriod = (items, targetPeriod, keepableSeatIdsByType = null) => {
        const options = allTicketOptions?.ticketOptions || [];
        const targetOptions = options.filter((o) => o.slotKey === targetPeriod);
        if (!targetOptions.length) return { remapped: items, blockedTypes: [], diff: 0 };
        const blockedTypes = new Set();
        let oldSum = 0;
        let newSum = 0;
        const seatFor = (item) =>
            item.ticketSeatId != null &&
            keepableSeatIdsByType?.get(item.ticketTypeId)?.has(item.ticketSeatId)
                ? { ticketSeatId: item.ticketSeatId, ticketSeatName: item.ticketSeatName }
                : { ticketSeatId: null, ticketSeatName: '' };
        const remapped = items.map((item) => {
            if (item.shouldDelete) return item;
            const seatFields = seatFor(item);
            const currentOption = options.find((o) => o.id === item.ticketOptionId);
            if (!currentOption?.slotKey) return { ...item, ...seatFields }; // non-slot add-on
            if (currentOption.slotKey === targetPeriod) {
                oldSum += parseInt(item.customPriceCents, 10) || 0;
                newSum += parseInt(item.customPriceCents, 10) || 0;
                return { ...item, ...seatFields };
            }
            const type = (allTicketTypes?.ticketTypes || []).find(
                (tt) => tt.id === item.ticketTypeId,
            );
            const pricedOptions = type
                ? targetOptions.filter(
                      (option) => definePrice(type.priceMatrix, option.name) !== undefined,
                  )
                : [];
            const sameAgeBandOptions = pricedOptions.filter(
                (option) => option.ageBand === currentOption.ageBand,
            );
            const targetOption =
                sameAgeBandOptions.length === 1
                    ? sameAgeBandOptions[0]
                    : pricedOptions.length === 1
                      ? pricedOptions[0]
                      : undefined;
            const newPrice = targetOption
                ? definePrice(type.priceMatrix, targetOption.name)
                : undefined;
            if (newPrice === undefined) {
                blockedTypes.add(type?.name || item.ticketTypeName);
                return { ...item, ...seatFields };
            }
            oldSum += parseInt(item.customPriceCents, 10) || 0;
            newSum += newPrice;
            return {
                ...item,
                ...seatFields,
                ticketOptionId: targetOption.id,
                ticketOptionName: targetOption.name,
                customPriceCents: newPrice,
                price: newPrice,
            };
        });
        return { remapped, blockedTypes: [...blockedTypes], diff: newSum - oldSum };
    };

    const periodChangeImpact = (diff) =>
        diff === 0
            ? t('bookings.changePeriodNoCharge')
            : diff > 0
              ? t('bookings.changePeriodCharge', { amount: formatCentsToUsd(diff) })
              : t('bookings.changePeriodRefund', { amount: formatCentsToUsd(-diff) });

    const notifyPeriodBlocked = (blockedTypes, targetPeriod) =>
        notifyError(
            t('bookings.changePeriodNoPrice', {
                types: blockedTypes.join(', '),
                period: resolveSlotLabel(slotTemplate, { slotKey: targetPeriod }),
            }),
        );

    // Segmented "Rental period" control: convert within the rental-day group, with
    // a confirm that guards accidental toggle taps.
    const handleChangePeriod = async (targetPeriod) => {
        if (!targetPeriod || targetPeriod === currentPeriod) return;
        const targetTimeslot = periodTimeslots[targetPeriod];
        if (!targetTimeslot) return;

        // Seats still available in the target period keep their assignment.
        // GetFreeTicketSeats spans the rental blocking siblings and always
        // re-includes this booking's own seats, which is what lets an
        // AM-to-Full-Day upgrade stay on the same seat. If availability can't
        // be read, fall back to clearing every seat (the previous behavior);
        // the backend conflict check still guards the save either way.
        const seatedTypeIds = [
            ...new Set(
                updatedData
                    .filter((i) => !i.shouldDelete && i.ticketSeatId != null)
                    .map((i) => i.ticketTypeId),
            ),
        ];
        let keepableSeatIdsByType = null;
        try {
            keepableSeatIdsByType = new Map();
            for (const ticketTypeId of seatedTypeIds) {
                const res = await getFreeTicketSeats(showId, {
                    timeslotId: targetTimeslot.id,
                    ticketTypeId,
                    bookingId: Number(bookingId),
                    pageSize: 10000,
                });
                keepableSeatIdsByType.set(
                    ticketTypeId,
                    new Set((res.ticketSeats || []).map((s) => s.id)),
                );
            }
        } catch {
            keepableSeatIdsByType = null;
        }

        const remap = remapItemsToPeriod(updatedData, targetPeriod, keepableSeatIdsByType);
        if (remap.blockedTypes.length) {
            notifyPeriodBlocked(remap.blockedTypes, targetPeriod);
            return;
        }
        const targetLabel = resolveSlotLabel(slotTemplate, { slotKey: targetPeriod });
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('bookings.changePeriodTitle', { period: targetLabel }),
            content: t('bookings.changePeriodConfirm', {
                period: targetLabel,
                impact: periodChangeImpact(remap.diff),
            }),
            confirm: () => {
                if (!bookingUnlocked) setBookingUnlocked(true);
                setUpdatedData(remap.remapped);
                setNewTimeslotId(targetTimeslot.id);
            },
        });
    };
    // -----------------------------------------------------------------------

    const { data: ticketPermissions } = useGetBookingTicketPermissions(Number(bookingId), {
        query: {
            enabled: !!bookingId,
            onError: notifyError,
        },
    });

    const { data: showData } = useGetShow(showId, {
        query: {
            enabled: !!showId,
            onError: notifyError,
        },
    });

    useEffect(() => {
        if (!seatData) {
            return;
        }
        // need to be another mapFromDbToElement for cateringview, because the stroke color is different
        setElements(seatData?.ticketSeats.map(mapFromDbToElement));
    }, [seatData, seatLoading, setElements]);

    const {
        data: ordersData,
        isLoading: ordersLoading,
        refetch: reloadOrders,
    } = useGetOrders({
        optionalId: Number(bookingId),
        pageSize: 10000,
    });

    const pendingOrdersList = useMemo(
        () => (ordersData?.orders || []).filter((order) => order.status === 'pending'),
        [ordersData],
    );
    const hasAnyPendingOrders = !ordersLoading && pendingOrdersList.length > 0;
    const [isDiscarding, setIsDiscarding] = useState(false);

    const handleStartEdit = () => {
        if (!hasAnyPendingOrders) {
            setBookingUnlocked(true);
            return;
        }
        // Snapshot the agent's pending modification from current data BEFORE
        // we roll the order back, then rehydrate the editor with it.
        const seed = buildEditorSeed(pendingOrdersList, data?.bookingItems || []);
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('bookingitems.discardPendingTitle'),
            content: t('bookingitems.discardPendingMessage'),
            warningContent: t('bookingitems.discardPendingWarning'),
            confirm: async () => {
                setIsDiscarding(true);
                try {
                    for (const order of pendingOrdersList) {
                        await manuallyTriggerRollback(order.id, { skipBookingCancel: true });
                    }
                    const refreshed = await reloadBookingItems();
                    const baseItems = mapBookingItemsToUpdated(refreshed.data?.bookingItems || []);
                    setUpdatedData(
                        applyEditorSeed(
                            baseItems,
                            seed,
                            allTicketTypes?.ticketTypes || [],
                            allTicketOptions?.ticketOptions || [],
                            seatData?.ticketSeats || [],
                        ),
                    );
                    setBookingUnlocked(true);
                    reloadOrders();
                    reloadParentOrders?.();
                    reloadData?.();
                } catch (err) {
                    notifyError(err);
                } finally {
                    setIsDiscarding(false);
                }
            },
        });
    };

    const commissionMatrix = useMemo(
        () => ticketPermissions?.commissionMatrix || {},
        [ticketPermissions],
    );
    // The viewer's matrix (commissionMatrix) gates which combos may be added;
    // net-rate PRICING follows the booking agent's matrix, which the backend
    // returns separately (they differ only when an admin edits an agent's
    // booking).
    const pricingCommissionMatrix = ticketPermissions?.agentCommissionMatrix || commissionMatrix;
    const isNetRate = booking?.netRate || ticketPermissions?.isNetRate || false;
    const isRestrictedByCommissionGroup = !!ticketPermissions?.restrictedByCommissionGroup;
    const hasExplicitEnabledFlags = useMemo(
        () => matrixHasExplicitEnabledFlags(commissionMatrix),
        [commissionMatrix],
    );

    const isTicketCombinationAllowed = useMemo(() => {
        return (ticketTypeId, ticketOptionId) => {
            const ticketType = allTicketTypes?.ticketTypes?.find(
                (type) => type.id === ticketTypeId,
            );
            const ticketOption = allTicketOptions?.ticketOptions?.find(
                (option) => option.id === ticketOptionId,
            );

            if (!ticketType || !ticketOption) {
                return false;
            }
            if (!isRestrictedByCommissionGroup) {
                return true;
            }
            if (ticketType.guestVisible && ticketOption.guestVisible !== false) {
                return true;
            }
            if (!hasExplicitEnabledFlags) {
                // No explicit matrix — only guest-visible combos are allowed
                return false;
            }

            const matrixEntry = getMatrixEntry(
                commissionMatrix,
                ticketType.id,
                ticketOption.id,
                ticketOption.name,
            );

            return matrixEntry?.enabled === true;
        };
    }, [
        allTicketTypes,
        allTicketOptions,
        isRestrictedByCommissionGroup,
        hasExplicitEnabledFlags,
        commissionMatrix,
    ]);

    const filteredTicketTypes = useMemo(() => {
        const ticketTypes = allTicketTypes?.ticketTypes || [];
        const ticketOptions = allTicketOptions?.ticketOptions || [];
        if (!isRestrictedByCommissionGroup) {
            return ticketTypes;
        }
        return ticketTypes.filter((type) => {
            if (type.guestVisible) {
                return true;
            }
            return ticketOptions.some((option) => isTicketCombinationAllowed(type.id, option.id));
        });
    }, [
        allTicketTypes,
        allTicketOptions,
        isRestrictedByCommissionGroup,
        isTicketCombinationAllowed,
    ]);

    // Options the booking already has live tickets for. These must stay
    // selectable even when they fall outside the current period filter -
    // otherwise, after a rental slot change, the now-mismatched ticket
    // loses its row and can't be removed, and the save dead-ends on the
    // period guard. The filters below scope what can be ADDED; existing
    // tickets always keep a row so they can be removed.
    const presentOptionIds = new Set(
        updatedData.filter((item) => !item.shouldDelete).map((item) => item.ticketOptionId),
    );

    // Rental slot scoping: only offer options valid for this timeslot's slot
    // (plus any the booking already uses). Options without a slot (and all
    // non-rental timeslots) pass.
    const periodFilteredTicketOptions = (allTicketOptions?.ticketOptions || []).filter(
        (option) =>
            !option.slotKey || option.slotKey === targetSlotKey || presentOptionIds.has(option.id),
    );
    const filteredTicketOptions = isRestrictedByCommissionGroup
        ? periodFilteredTicketOptions.filter(
              (option) =>
                  presentOptionIds.has(option.id) ||
                  filteredTicketTypes.some((type) =>
                      isTicketCombinationAllowed(type.id, option.id),
                  ),
          )
        : periodFilteredTicketOptions;

    useEffect(() => {
        // Don't sync from data while a seed-driven edit is in flight: the
        // refetch after rollback (orders/parent reloads) would clobber the
        // merged seed before the agent can save.
        if (bookingUnlocked) return;
        if (data?.bookingItems) {
            setUpdatedData(mapBookingItemsToUpdated(data.bookingItems));
        }
    }, [data, bookingUnlocked]);

    const autoOpenedRef = useRef(false);
    useEffect(() => {
        if (autoOpenedRef.current || bookingUnlocked) return;
        if (!data?.bookingItems || ordersLoading || !booking) return;
        if (readOnly || booking.status === BOOKING_STATUSES.CANCELLED || !!booking.invoiceId)
            return;
        if (!hasAnyPendingOrders) {
            autoOpenedRef.current = true;
            setBookingUnlocked(true);
        }
    }, [
        data,
        ordersLoading,
        booking,
        readOnly,
        hasAnyPendingOrders,
        bookingUnlocked,
        setBookingUnlocked,
    ]);

    useEffect(() => {
        const composition = location.state?.upgradeComposition;
        if (!bookingUnlocked || !composition) return;
        const ticketTypes = allTicketTypes?.ticketTypes;
        const ticketOptions = allTicketOptions?.ticketOptions;
        if (!ticketTypes || !ticketOptions) return;
        // Consume the one-shot router state so a re-render can't re-apply the
        // composition (partial-quantity upgrades would otherwise double-apply).
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
        const unpriced = composition.some((comp) => {
            const tt = ticketTypes.find((x) => x.id === comp.toTicketTypeId);
            const to = ticketOptions.find((x) => x.id === comp.toTicketOptionId);
            return getPriceFromMatrix(to?.name || '', tt?.priceMatrix) == null;
        });
        if (unpriced) {
            notifyError(t('upgrades.errors.priceUnavailable'));
            return;
        }
        setUpdatedData((prev) =>
            applyUpgradeComposition(prev, composition, ticketTypes, ticketOptions),
        );
    }, [bookingUnlocked, location, allTicketTypes, allTicketOptions, navigate, notifyError, t]);

    const hasChanges = useMemo(() => {
        if (newTimeslotId !== null) return true;
        const baseline = mapBookingItemsToUpdated(data?.bookingItems || []);
        if (updatedData.length !== baseline.length) return true;
        const byOldId = new Map(baseline.map((item) => [item.oldId, item]));
        return updatedData.some((item) => {
            if (!item.oldId) return true;
            const base = byOldId.get(item.oldId);
            if (!base) return true;
            return (
                item.shouldDelete ||
                item.ticketTypeId !== base.ticketTypeId ||
                item.ticketOptionId !== base.ticketOptionId ||
                item.ticketSeatId !== base.ticketSeatId ||
                item.customPriceCents !== base.customPriceCents ||
                (item.note || '') !== (base.note || '') ||
                (item.checkinNote || '') !== (base.checkinNote || '')
            );
        });
    }, [updatedData, data?.bookingItems, newTimeslotId]);

    // Handle timeslot change: clear all seat assignments and refetch free seats
    const handleTimeslotChange = (selectedTimeslotId) => {
        if (selectedTimeslotId === originalTimeslotId) {
            // Reverting to original - restore original data
            setNewTimeslotId(null);
            setShowTimeslotPicker(false);
            if (data?.bookingItems) {
                setUpdatedData(mapBookingItemsToUpdated(data.bookingItems));
            }
            return;
        }
        // If the new slot is a rental slot that differs from the booking's
        // tickets, convert them to that slot (mixing is not allowed) so a
        // Change Date/Time across slots does not strand old-slot tickets.
        const newTimeslot = (timeslotsData?.timeslots || []).find(
            (ts) => ts.id === selectedTimeslotId,
        );
        const newPeriod = newTimeslot?.slotKey ?? null;
        const options = allTicketOptions?.ticketOptions || [];
        const needsPeriodRemap =
            !!newPeriod &&
            updatedData.some((item) => {
                if (item.shouldDelete) return false;
                const opt = options.find((o) => o.id === item.ticketOptionId);
                return opt?.slotKey && opt.slotKey !== newPeriod;
            });
        if (needsPeriodRemap) {
            const remap = remapItemsToPeriod(updatedData, newPeriod);
            if (remap.blockedTypes.length) {
                notifyPeriodBlocked(remap.blockedTypes, newPeriod);
                return; // abort - cannot convert, leave the timeslot unchanged
            }
            setUpdatedData(remap.remapped); // remap also clears seats
        } else {
            // Same period or non-rental: just clear seats (they belong to the old slot).
            setUpdatedData((prev) =>
                prev.map((item) => ({ ...item, ticketSeatId: null, ticketSeatName: '' })),
            );
        }
        setNewTimeslotId(selectedTimeslotId);
        setShowTimeslotPicker(false);
        setSelectedSeatId(null);
        setEditTicketIdx(null);
    };

    // Find the selected timeslot's start time for display
    const selectedTimeslotInfo = useMemo(() => {
        if (!newTimeslotId || !timeslotsData?.timeslots) return null;
        return timeslotsData.timeslots.find((ts) => ts.id === newTimeslotId);
    }, [newTimeslotId, timeslotsData]);

    // Check which ticket types have seats configured
    const ticketTypesWithSeats = useMemo(() => {
        if (!allTicketTypes?.ticketTypes) return new Set();
        return new Set(
            allTicketTypes.ticketTypes
                .filter((type) => type.seats && type.seats.length > 0)
                .map((type) => type.id),
        );
    }, [allTicketTypes]);

    // Check if any non-deleted tickets need seats but don't have them
    const hasUnseatedTickets = useMemo(() => {
        return updatedData.some(
            (item) =>
                !item.shouldDelete &&
                !item.ticketSeatId &&
                item.ticketTypeId &&
                ticketTypesWithSeats.has(item.ticketTypeId),
        );
    }, [updatedData, ticketTypesWithSeats]);

    const [isAutoSeating, setIsAutoSeating] = useState(false);

    // Auto-seat a single ticket item
    const handleAutoSeatOne = async (item) => {
        if (!timeslotId || !item.ticketTypeId) return;
        setIsAutoSeating(true);
        try {
            const currentSeats = updatedData
                .filter((i) => !i.shouldDelete && i.ticketSeatId)
                .map((i) => ({ id: i.ticketSeatId, ticketTypeId: i.ticketTypeId }));

            const result = await getAutoSeating(timeslotId, {
                currentSeats,
                requiredSeats: [{ ticketTypeId: item.ticketTypeId, quantity: 1 }],
            });

            const suggestion = result.suggestions?.find(
                (s) => s.ticketTypeId === item.ticketTypeId,
            );
            if (suggestion?.seats?.[0]) {
                const seat = suggestion.seats[0];
                setUpdatedData((prev) =>
                    prev.map((i) =>
                        i === item || (i.id === item.id && i.oldId === item.oldId)
                            ? { ...i, ticketSeatId: seat.id, ticketSeatName: seat.name }
                            : i,
                    ),
                );
            } else {
                notifyError(t('bookings.autoSeatFailed'));
            }
        } catch (_err) {
            notifyError(t('bookings.autoSeatFailed'));
        } finally {
            setIsAutoSeating(false);
        }
    };

    // Auto-seat items and return the result - shared by handleAutoSeatAll and prepareConfirmData
    const autoSeatAndGetItems = async (items) => {
        if (!timeslotId) return items;

        const currentSeats = items
            .filter((i) => !i.shouldDelete && i.ticketSeatId)
            .map((i) => ({ id: i.ticketSeatId, ticketTypeId: i.ticketTypeId }));

        const unseatedByType = {};
        items.forEach((item) => {
            if (
                !item.shouldDelete &&
                !item.ticketSeatId &&
                item.ticketTypeId &&
                ticketTypesWithSeats.has(item.ticketTypeId)
            ) {
                unseatedByType[item.ticketTypeId] = (unseatedByType[item.ticketTypeId] || 0) + 1;
            }
        });

        const requiredSeats = Object.entries(unseatedByType).map(([ticketTypeId, quantity]) => ({
            ticketTypeId: Number(ticketTypeId),
            quantity,
        }));

        if (requiredSeats.length === 0) return items;

        const result = await getAutoSeating(timeslotId, {
            currentSeats,
            requiredSeats,
        });

        const seatsByType = {};
        for (const suggestion of result.suggestions || []) {
            seatsByType[suggestion.ticketTypeId] = [...(suggestion.seats || [])];
        }

        return items.map((item) => {
            if (
                !item.shouldDelete &&
                !item.ticketSeatId &&
                item.ticketTypeId &&
                seatsByType[item.ticketTypeId]?.length > 0
            ) {
                const seat = seatsByType[item.ticketTypeId].shift();
                return { ...item, ticketSeatId: seat.id, ticketSeatName: seat.name };
            }
            return item;
        });
    };

    const handleAutoSeatAll = async () => {
        if (!timeslotId) return;
        setIsAutoSeating(true);
        try {
            const seated = await autoSeatAndGetItems(updatedData);
            setUpdatedData(seated);
        } catch (_err) {
            notifyError(t('bookings.autoSeatFailed'));
        } finally {
            setIsAutoSeating(false);
        }
    };

    const delayedSeatAssignmentEnabled = !!showData?.marketing?.delayedSeatAssignmentEnabled;

    const prepareConfirmData = async () => {
        let itemsToSubmit = updatedData;
        if (useSimplifiedTickets && hasUnseatedTickets && !delayedSeatAssignmentEnabled) {
            try {
                itemsToSubmit = await autoSeatAndGetItems(updatedData);
            } catch (_err) {
                if (!canOverbook) {
                    notifyError(t('bookings.autoSeatFailed'));
                    return null;
                }
            }
        }

        const changeList = generateChangeList(
            data?.bookingItems || [],
            itemsToSubmit,
            showData?.fees || [],
            booking?.source === 'online',
            isNetRate,
            pricingCommissionMatrix,
        );

        const timeslotChanged = newTimeslotId && newTimeslotId !== originalTimeslotId;
        if (timeslotChanged && selectedTimeslotInfo) {
            changeList.unshift(
                `${t('bookings.timeslotChanged')}: ${formatDateTimeInVenueTz(booking?.timeslot?.startTime)} → ${formatDateTimeInVenueTz(selectedTimeslotInfo.startTime)}`,
            );
        }

        if (isRestrictedByCommissionGroup) {
            const hasForbiddenExistingItems = (data?.bookingItems || []).some(
                (item) => !isTicketCombinationAllowed(item.ticketTypeId, item.ticketOptionId),
            );
            if (hasForbiddenExistingItems) {
                notifyError(t('errors.unauthorizedToModifyTickets'));
                return null;
            }
            const hasForbiddenNewItems = itemsToSubmit
                .filter((item) => !item.shouldDelete)
                .some(
                    (item) => !isTicketCombinationAllowed(item.ticketTypeId, item.ticketOptionId),
                );
            if (hasForbiddenNewItems) {
                notifyError(t('errors.unauthorizedToModifyTickets'));
                return null;
            }
        }

        const oldPriceSum = (data?.bookingItems || []).reduce(
            (sum, item) => sum + (item.price || 0),
            0,
        );
        const oldFinalPriceSum = (data?.bookingItems || []).reduce(
            (sum, item) => sum + (item.finalPrice || 0),
            0,
        );
        const newPriceSum = itemsToSubmit
            .filter((item) => !item.shouldDelete)
            .reduce((sum, item) => sum + (item.customPriceCents ? item.customPriceCents : 0), 0);
        let newFinalPriceSum = 0;
        for (let i = 0; i < itemsToSubmit.length; i++) {
            const item = itemsToSubmit[i];
            if (item.shouldDelete) continue;
            newFinalPriceSum += previewItemFinalPriceCents(item, {
                netRate: isNetRate,
                commissionMatrix: pricingCommissionMatrix,
                showFees: showData?.fees || [],
                isOnline: booking?.source === 'online',
            });
        }

        const onlyCanBuyAloneTickets = itemsToSubmit
            .filter((item) => !item.shouldDelete)
            .every((item) => item.ticketTypeCanBuyAlone === false);

        const isUpgrade = newFinalPriceSum > oldFinalPriceSum;
        const additionalAmount = isUpgrade ? newFinalPriceSum - oldFinalPriceSum : 0;

        return {
            changeList,
            itemsToSubmit,
            timeslotChanged,
            isUpgrade,
            additionalAmount,
            oldPriceSum,
            newPriceSum,
            oldFinalPriceSum,
            newFinalPriceSum,
            onlyCanBuyAloneTickets,
            isNetRate,
            showId,
            customerEmail: booking?.email || '',
        };
    };

    const executeSave = async (itemsToSubmit, timeslotChanged, couponCode, sendNotification) => {
        setIsSubmitting(true);
        try {
            await changeBookingItems(
                Number(bookingId),
                {
                    bookingItems: itemsToSubmit.map((item) => ({
                        ticketOptionId: item.ticketOptionId,
                        ticketSeatId: item.ticketSeatId || null,
                        ticketTypeId: item.ticketTypeId,
                        oldId: item.oldId || null,
                        customPriceCents: parseFloat(item.customPriceCents) || 0,
                        shouldDelete: item.shouldDelete || false,
                        checkinNote: item.checkinNote || '',
                        note: item.note || '',
                        canBuyAlone: item.ticketTypeCanBuyAlone || false,
                    })),
                    couponCode: couponCode || null,
                    timeslotId: timeslotChanged ? newTimeslotId : null,
                },
                { sendNotification },
            );
            setSaveError(null);
            setBookingUnlocked(false);
            setNewTimeslotId(null);
            setShowTimeslotPicker(false);
        } catch (err) {
            setSaveError(err);
            // Re-throw so the unified save (EditBookingPage.handleSharedSave)
            // owns the single success/error toast and the single refresh
            // after both the contact and ticket writes settle.
            throw err;
        } finally {
            setIsSubmitting(false);
        }
    };

    let editTicketItem = editTicketIdx !== null ? updatedData[editTicketIdx] : null;

    // if this a is a net new BUT free seat, we should prefll the editTicketItem
    if (
        !editTicketItem &&
        selectedSeatId &&
        bookingUnlocked &&
        updatedData?.findIndex((item) => item.ticketSeatId === selectedSeatId) === -1
    ) {
        const seat = elements.find((el) => el.id === selectedSeatId);
        editTicketItem = { ...createEmptyTicketItem(seat.id, seat.name || ''), isNew: true };
    }

    const effectiveTimeslot = timeslotsData?.timeslots?.find(
        (ts) => ts.id === (newTimeslotId || originalTimeslotId),
    );
    const capacityOverflows = computeCapacityOverflows({
        updatedData,
        blocks: effectiveTimeslot?.ticketTypeBlocks || [],
        t,
        timeslotIsChanging: !!newTimeslotId && newTimeslotId !== originalTimeslotId,
    });
    const isCapacityBlocked = capacityOverflows.length > 0 && !canOverbook;

    const remainingTicketTypeIds = new Set(
        (updatedData || [])
            .filter((item) => !item.shouldDelete && item.ticketTypeId)
            .map((item) => item.ticketTypeId),
    );
    const crossSellingBlocked = !canCrossSelling && remainingTicketTypeIds.size > 1;

    useImperativeHandle(ref, () => ({
        handleDiscardChanges,
        prepareConfirmData,
        executeSave,
    }));

    useEffect(() => {
        const hasPendingChanges =
            bookingUnlocked && (hasChanges || isCapacityBlocked || crossSellingBlocked);
        const saveDisabled = isCapacityBlocked || crossSellingBlocked;
        onPendingChangesChange?.({ hasPendingChanges, saveDisabled });
    }, [bookingUnlocked, hasChanges, isCapacityBlocked, crossSellingBlocked]); // eslint-disable-line react-hooks/exhaustive-deps

    const paxSummary = useMemo(() => {
        const active = updatedData.filter((i) => !i.shouldDelete);
        if (!active.length) return null;
        const byOption = new Map();
        for (const item of active) {
            // ticketOptionName holds the demographic (Adult/Child/Toddler) in
            // this data model; ticketTypeName is the seating tier. The client
            // wants the reservation broken down by demographic.
            const name = item.ticketOptionName || '';
            byOption.set(name, (byOption.get(name) || 0) + 1);
        }
        const parts = [...byOption.entries()].map(([name, count]) => `${count} ${name}`);
        return t('bookingitems.paxSummary', {
            count: active.length,
            breakdown: parts.join(', '),
        });
    }, [updatedData, t]);

    const handleDiscardChanges = () => {
        setNewTimeslotId(null);
        setShowTimeslotPicker(false);
        setUpdatedData(mapBookingItemsToUpdated(data?.bookingItems));
        setSelectedSeatId(null);
        setEditTicketIdx(null);
    };

    const handleCancelEdit = () => {
        setBookingUnlocked(false);
        setNewTimeslotId(null);
        setShowTimeslotPicker(false);
        setSimplifiedOverride(defaultSimplified);
        setUpdatedData(mapBookingItemsToUpdated(data?.bookingItems));
        setSelectedSeatId(null);
        setEditTicketIdx(null);
        reloadOrders();
        reloadParentOrders?.();
    };

    return (
        <Grid2 size={{ xs: 12 }}>
            <BlockWithTitle
                title={t('bookingitems.title')}
                headerRight={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                        {canCancel && (
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={operatingDisabled}
                                onClick={onCancelReservation}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {t('common.cancel')}
                            </Button>
                        )}
                        {canRefund && (
                            <Button
                                variant="outlined"
                                disabled={operatingDisabled}
                                onClick={onRefundReservation}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {t('common.refund')}
                            </Button>
                        )}
                        {!readOnly && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                                >
                                    <MoreHorizIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchorEl}
                                    open={Boolean(menuAnchorEl)}
                                    onClose={() => setMenuAnchorEl(null)}
                                >
                                    {!bookingUnlocked && (
                                        <MenuItem
                                            disabled={
                                                ordersLoading ||
                                                booking.status === BOOKING_STATUSES.CANCELLED ||
                                                !!booking.invoiceId ||
                                                isDiscarding
                                            }
                                            onClick={() => {
                                                handleStartEdit();
                                                setMenuAnchorEl(null);
                                            }}
                                        >
                                            {t('bookingitems.editSeats')}
                                        </MenuItem>
                                    )}
                                    {bookingUnlocked && !lockedToSimplified && (
                                        <MenuItem
                                            onClick={() => {
                                                setSimplifiedOverride((prev) => !prev);
                                                setMenuAnchorEl(null);
                                            }}
                                        >
                                            {t(
                                                useSimplifiedTickets
                                                    ? 'bookingitems.simplified.switchToDetailed'
                                                    : 'bookingitems.simplified.switchToSimplified',
                                            )}
                                        </MenuItem>
                                    )}
                                    {bookingUnlocked && (
                                        <MenuItem
                                            onClick={() => {
                                                handleCancelEdit();
                                                setMenuAnchorEl(null);
                                            }}
                                        >
                                            {t('bookingitems.cancelEdit')}
                                        </MenuItem>
                                    )}
                                </Menu>
                            </>
                        )}
                    </Box>
                }
                subHeader={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {newTimeslotId && selectedTimeslotInfo ? (
                                    <Chip
                                        label={`${t('bookings.timeslotChanged')}: ${formatDateTimeInVenueTz(selectedTimeslotInfo.startTime)}`}
                                        color="warning"
                                        size="small"
                                        onDelete={() => handleTimeslotChange(originalTimeslotId)}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        {booking?.timeslot?.startTime
                                            ? formatDateTimeInVenueTz(booking.timeslot.startTime)
                                            : ''}
                                    </Typography>
                                )}
                                {!limitedModification &&
                                    !booking?.invoiceId &&
                                    booking?.status !== BOOKING_STATUSES.CANCELLED && (
                                        <Tooltip
                                            title={
                                                hasAnyPendingOrders
                                                    ? t('bookingitems.warningPendingOrders')
                                                    : ''
                                            }
                                        >
                                            <span>
                                                <Chip
                                                    icon={<CalendarMonthIcon />}
                                                    label={t('bookings.changeDate')}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        if (!bookingUnlocked)
                                                            setBookingUnlocked(true);
                                                        setShowTimeslotPicker(true);
                                                    }}
                                                    disabled={hasAnyPendingOrders}
                                                    clickable={!hasAnyPendingOrders}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                            </Box>
                        </Box>
                        {paxSummary && (
                            <Typography variant="body2" color="text.secondary">
                                {paxSummary}
                            </Typography>
                        )}
                        {showPeriodSwitcher && (
                            <Box sx={{ mt: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.25 }}
                                >
                                    {t('bookings.changePeriodLabel')}
                                </Typography>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={currentPeriod}
                                    onChange={(_e, p) => handleChangePeriod(p)}
                                    orientation={isNarrowScreen ? 'vertical' : 'horizontal'}
                                    fullWidth={isNarrowScreen}
                                >
                                    {availablePeriods.map((p) => (
                                        <ToggleButton key={p} value={p}>
                                            {resolveSlotLabel(slotTemplate, { slotKey: p })}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        )}
                    </Box>
                }
            >
                <Grid2
                    container
                    spacing={2}
                    justifyItems="stretch"
                    flexDirection="column"
                    width={'100%'}
                    position="relative"
                >
                    {bookingUnlocked && capacityOverflows.length > 0 && (
                        <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
                            {t(
                                canOverbook
                                    ? 'bookings.capacityWarningOverbook'
                                    : 'bookings.capacityWarning',
                                { details: capacityOverflows.join(', ') },
                            )}
                        </Alert>
                    )}
                    {bookingUnlocked && crossSellingBlocked && (
                        <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
                            {t('errors.agentCannotMixTicketTypes')}
                        </Alert>
                    )}
                    {/* Timeslot calendar picker */}
                    {bookingUnlocked && !limitedModification && showTimeslotPicker && (
                        <Box sx={{ mb: 1, width: '100%' }}>
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                {t('bookings.changeDateWarning')}
                            </Alert>
                            <TimeslotCalendar
                                timeslots={timeslotsData?.timeslots || []}
                                selectedTimeslot={newTimeslotId || originalTimeslotId}
                                disableCancelledSelection
                                onTimeslotSelect={handleTimeslotChange}
                                slotTemplate={slotTemplate}
                            />
                        </Box>
                    )}
                    {/* Auto-seat all button - shown when tickets are missing seats */}
                    {bookingUnlocked && hasUnseatedTickets && !useSimplifiedTickets && (
                        <Box sx={{ width: '100%', mb: 1, textAlign: 'left' }}>
                            <Chip
                                icon={<AutoFixHighIcon />}
                                label={t('bookings.autoSeatAll')}
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={handleAutoSeatAll}
                                clickable
                                disabled={isAutoSeating || isSubmitting}
                            />
                        </Box>
                    )}
                    {bookingUnlocked && canModifySeating && !useSimplifiedTickets && (
                        <SvgViewer
                            elements={displayElements}
                            colorMap={generateSeatColorMap(
                                elements,
                                updatedData,
                                selectedSeatId,
                                freeSeatsData,
                                filteredTicketTypes,
                            )}
                            onClickElement={(id) => {
                                setMapPopupVisible(true);
                                // let's do some checks first
                                if (elements?.find((el) => el.id === id && !el.bookable)) {
                                    // non bookable, just ignore the click
                                    return;
                                }
                                const isOnFreeOrBooked =
                                    freeSeatsData?.ticketSeats?.find((s) => s.id === id) !==
                                        undefined ||
                                    updatedData.find((item) => item.ticketSeatId === id) !==
                                        undefined;
                                if (!isOnFreeOrBooked) {
                                    // not on free or booked seat, ignore the click
                                    return;
                                }

                                setSelectedSeatId(id);
                                // find the ticket if we have one with this seat
                                if (bookingUnlocked) {
                                    const idx = updatedData.findIndex(
                                        (item) => item.ticketSeatId === id,
                                    );
                                    setEditTicketIdx(idx !== -1 ? idx : null);
                                }
                            }}
                            inCartSeatIds={[]}
                        >
                            {editTicketItem && mapPopupVisible ? (
                                <OneTicketEditPopup
                                    key={editTicketIdx}
                                    showFees={showData?.fees || []}
                                    isOnline={booking?.source === 'online'}
                                    values={editTicketItem}
                                    timeslotId={timeslotId}
                                    bookingId={bookingId}
                                    isSubmitting={isSubmitting}
                                    limitedModification={limitedModification}
                                    allTicketTypes={filteredTicketTypes}
                                    allTicketOptions={filteredTicketOptions}
                                    priceOverrides={priceOverrides}
                                    isTicketCombinationAllowed={isTicketCombinationAllowed}
                                    netRate={isNetRate}
                                    commissionMatrix={pricingCommissionMatrix}
                                    closePopup={() => {
                                        setEditTicketIdx(null);
                                        setSelectedSeatId(null);
                                        setMapPopupVisible(false);
                                    }}
                                    setFieldValue={(field, value) => {
                                        if (editTicketIdx === null) {
                                            return;
                                        }
                                        setUpdatedData((oldData) => {
                                            return oldData.map((item, index) =>
                                                index === editTicketIdx
                                                    ? {
                                                          ...item,
                                                          [field]: value,
                                                      }
                                                    : item,
                                            );
                                        });
                                    }}
                                    addAddedTicket={(item) => {
                                        setMapPopupVisible(false);
                                        setUpdatedData((oldData) => [...oldData, item]);
                                        setEditTicketIdx(
                                            updatedData.length + 1, // it's +1 because we are not yet in the state
                                        );
                                    }}
                                    removeAddedTicket={() => {
                                        // this one is for tickets that would be new in this modification but we remove them
                                        setUpdatedData((oldData) => {
                                            return oldData.filter(
                                                (_item, index) => index !== editTicketIdx,
                                            );
                                        });
                                        setEditTicketIdx(null);
                                        setSelectedSeatId(null);
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        background: 'transparent',
                                        width: '200px',
                                        height: '100px',
                                        pointerEvents: 'none',
                                        opacity: 0,
                                    }}
                                >
                                    {t('common.placeholder')}
                                </div>
                            )}
                        </SvgViewer>
                    )}
                    {bookingUnlocked && saveError && (
                        <Box sx={{ color: 'red', marginBottom: 2 }}>
                            {t('bookingitems.modifyTickets.error', {
                                error: formatErrorToString(saveError),
                            })}
                        </Box>
                    )}
                    {bookingUnlocked && useSimplifiedTickets && !limitedModification && (
                        <SimplifiedTicketManager
                            updatedData={updatedData}
                            setUpdatedData={setUpdatedData}
                            allTicketTypes={filteredTicketTypes}
                            allTicketOptions={filteredTicketOptions}
                            priceOverrides={priceOverrides}
                            showFees={showData?.fees || EMPTY_FEES}
                            isOnline={booking?.source === 'online'}
                            hasUnseatedTickets={hasUnseatedTickets}
                            delayedSeatAssignment={delayedSeatAssignmentEnabled}
                            isTicketCombinationAllowed={isTicketCombinationAllowed}
                            netRate={isNetRate}
                            commissionMatrix={pricingCommissionMatrix}
                        />
                    )}
                    {/* Full ticket list + editor */}
                    <Grid2 item xs={12}>
                        <Grid2 container spacing={2} justifyItems="stretch" flexDirection="column">
                            {!loading &&
                                updatedData?.map((item, idx) => {
                                    if (
                                        editTicketIdx === idx &&
                                        bookingUnlocked &&
                                        (!useSimplifiedTickets || canModifySeating)
                                    ) {
                                        return (
                                            <OneTicketEdit
                                                key={item.id}
                                                values={item}
                                                timeslotId={timeslotId}
                                                bookingId={bookingId}
                                                showFees={showData?.fees || []}
                                                isOnline={booking?.source === 'online'}
                                                isSubmitting={isSubmitting}
                                                showId={showId}
                                                priceOverrides={priceOverrides}
                                                netRate={isNetRate}
                                                commissionMatrix={pricingCommissionMatrix}
                                                limitedModification={
                                                    limitedModification || useSimplifiedTickets
                                                }
                                                setFieldValue={(field, value) => {
                                                    setUpdatedData((oldData) => {
                                                        return oldData.map((item, index) =>
                                                            index === idx
                                                                ? {
                                                                      ...item,
                                                                      [field]: value,
                                                                  }
                                                                : item,
                                                        );
                                                    });
                                                }}
                                                removeAddedTicket={() => {
                                                    // this one is for tickets that would be new in this modification but we remove them
                                                    setUpdatedData((oldData) => {
                                                        return oldData.filter(
                                                            (_item, index) => index !== idx,
                                                        );
                                                    });
                                                    setEditTicketIdx(null);
                                                    setSelectedSeatId(null);
                                                }}
                                                allTicketTypes={filteredTicketTypes}
                                                allTicketOptions={filteredTicketOptions}
                                                isTicketCombinationAllowed={
                                                    isTicketCombinationAllowed
                                                }
                                                onAutoSeat={handleAutoSeatOne}
                                                canModifySeating={canModifySeating}
                                                canCrossTicketType={canCrossTicketType}
                                                excludedSeatIds={excludedSeatIds}
                                            />
                                        );
                                    }
                                    const ticketType = filteredTicketTypes?.find(
                                        (tt) => tt.id === item.ticketTypeId,
                                    );
                                    const hasSeatMismatch =
                                        item.ticketSeatId &&
                                        ticketType?.seats?.length > 0 &&
                                        !ticketType.seats.some(
                                            (s) => Number(s.id) === Number(item.ticketSeatId),
                                        );
                                    return (
                                        <OneTicket
                                            key={item.id}
                                            item={item}
                                            showSeatName={canModifySeating}
                                            hasSeatMismatch={!!hasSeatMismatch}
                                            onClick={() => {
                                                if (
                                                    bookingUnlocked &&
                                                    (!useSimplifiedTickets || canModifySeating)
                                                ) {
                                                    setMapPopupVisible(false);
                                                    setEditTicketIdx(idx);
                                                    setSelectedSeatId(item.ticketSeatId);
                                                }
                                            }}
                                        />
                                    );
                                })}
                        </Grid2>
                    </Grid2>
                    {bookingUnlocked && !limitedModification && !useSimplifiedTickets && (
                        <>
                            {targetSlotKey && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={resolveSlotLabel(slotTemplate, {
                                        slotKey: targetSlotKey,
                                    })}
                                    sx={{ alignSelf: 'flex-start' }}
                                />
                            )}
                            <BulkAddTicketsWizard
                                timeslotId={timeslotId}
                                slotKey={targetSlotKey}
                                allTicketTypes={filteredTicketTypes}
                                allTicketOptions={filteredTicketOptions}
                                priceOverrides={priceOverrides}
                                existingItems={updatedData}
                                isTicketCombinationAllowed={isTicketCombinationAllowed}
                                delayedSeatAssignment={delayedSeatAssignmentEnabled}
                                onAddItems={(newItems) => {
                                    setUpdatedData([...updatedData, ...newItems]);
                                }}
                            />
                        </>
                    )}
                </Grid2>
            </BlockWithTitle>
        </Grid2>
    );
});

export default BookingItemsDetails;
